import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { DocumentOverviewItemDto, IDocumentOverviewItemDto, IFolderOverviewItemDto, isFolder } from '../../../config/documentConfig'
import DocumentCell from './DocumentCell'
import FolderCell from './FolderCell'
import { SearchResults } from './DocumentList'
import { ISelection, mergeStyleSets } from '@fluentui/react'

export type DocumentListItemProps<ItemT> = Props<ItemT> & {
    index: number
    isSelected: boolean
}

type Props<ItemT = DocumentOverviewItemDto> = {
    item: ItemT
    items: DocumentOverviewItemDto[]
    searchResults: SearchResults | undefined
    isExpanded: (folderId: string) => boolean
    setExpanded: (folderId: string, value?: boolean) => void
    selection: ISelection<DocumentOverviewItemDto>
    isParentSelected: boolean
    reset: () => void // Callback for when the view needs to be updated.
}

const searchResultsContains = (searchResults: SearchResults, id: string): boolean =>
    !searchResults || searchResults.matchIds.has(id) || searchResults.folderIds.has(id)

export const documentStyles = mergeStyleSets({
    largeDate: { minWidth: 80, textAlign: 'right' },
    icon: {
        display: 'flex',
        alignItems: 'center',
        height: 20,
        selectors: {
            '&:hover': { textDecoration: 'none' },
            '&:hover:active': { textDecoration: 'none' },
        },
    },
})

export default function DocumentListItem(props: Props): ReactElement {
    const isSelected = useMemo(() => props.selection.isKeySelected(props.item.id), [props.selection.getSelectedIndices()])
    const index = useMemo(() => props.selection.getItemIndex(props.item.id), [props.selection])

    if (!searchResultsContains(props.searchResults, props.item.id)) {
        return <></>
    }

    return isFolder(props.item) ? (
        <FolderCell {...(props as DocumentListItemProps<IFolderOverviewItemDto>)} index={index} isSelected={isSelected} reset={props.reset} />
    ) : (
        <DocumentCell {...(props as DocumentListItemProps<IDocumentOverviewItemDto>)} index={index} isSelected={isSelected} reset={props.reset} />
    )
}
